import type { ProductTypeAssessmentPublic } from "@repo/shared/types";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { T } from "./product-assessment-questionnaire-modal.i18n";

type ProductAssessmentQuestionnaireModalProps = {
  assessmentLabel: string;
  questions: NonNullable<ProductTypeAssessmentPublic["questions"]>;
  /** The last saved answers for this category — the draft starts from them. */
  initialAnswers: Record<string, string>;
  /** Read-only (completed instance): radios disabled, footer only closes. */
  disabled?: boolean;
  /** Sends the whole answer set in one request; the modal closes on success. */
  onSubmit: (answers: Record<string, string>) => Promise<unknown>;
  onClose: () => void;
};

/**
 * Questionnaire runner: every question on one screen, a
 * local draft seeded from the stored answers, and a single submit on "Done".
 * Nothing is sent while answering; closing with X / ESC discards the draft
 * without a confirm. Mount per open so a reopen starts from the saved answers.
 */
export function ProductAssessmentQuestionnaireModal({
  assessmentLabel,
  questions,
  initialAnswers,
  disabled = false,
  onSubmit,
  onClose,
}: ProductAssessmentQuestionnaireModalProps) {
  const localize = useLocalize();
  const t = useT(T);

  const [draft, setDraft] = useState<Record<string, string>>(initialAnswers);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isComplete = questions.every((q) => !!draft[q.id]);

  const handleDone = async () => {
    if (!isComplete || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit(draft);
      onClose();
    } catch (error) {
      log.error({ err: error }, "failed to submit questionnaire answers");
      toast.error(t.submitFailed);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isSubmitting) onClose();
      }}
    >
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{assessmentLabel}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {questions.map((question) => (
            <div key={question.id} className="space-y-2">
              <Label className="font-normal">{localize(question.labelI18n)}</Label>
              <RadioGroup
                value={draft[question.id] ?? ""}
                onValueChange={(value) => setDraft((prev) => ({ ...prev, [question.id]: value }))}
                disabled={disabled || isSubmitting}
              >
                {question.options.map((option) => (
                  <OptionRow key={option.id}>
                    <RadioGroupItem
                      value={option.id}
                      id={`q-${question.id}-${option.id}`}
                      disabled={disabled || isSubmitting}
                    />
                    <Label
                      htmlFor={`q-${question.id}-${option.id}`}
                      className="flex-1 font-normal cursor-pointer"
                    >
                      {localize(option.labelI18n)}
                    </Label>
                  </OptionRow>
                ))}
              </RadioGroup>
            </div>
          ))}
        </div>
        <DialogFooter>
          {disabled ? (
            <Button variant="outline" onClick={onClose}>
              {t.close}
            </Button>
          ) : (
            <Button onClick={handleDone} disabled={!isComplete} loading={isSubmitting}>
              {t.done}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
